import {SubscriptionPriceBoxNoticeData} from '@udemy-v2/notices';
import {ConsumerSubscriptionPlan} from '@udemy-v2/subscription-graphql';
import {useFormatCurrency} from '@udemy-v2/react-currency-formatting';

/**
 * Enum containing all the possible field names that could be interpolated into a given field for a subscription
 * discount notice.
 */
export enum NoticeInterpolation {
    SUBS_ANNUAL_OFFER_DISCOUNT_PRICE = 'subs_annual_offer_discount_price',
    SUBS_ANNUAL_OFFER_MONTHLY_DISCOUNT_PRICE = 'subs_annual_offer_monthly_discount_price',
    SUBS_ANNUAL_OFFER_MONTHLY_NON_DISCOUNT_PRICE = 'subs_annual_offer_monthly_non_discount_price',
    SUBS_MONTHLY_OFFER_DISCOUNT_PRICE = 'subs_monthly_offer_discount_price',
    SUBS_ANNUAL_OFFER_DISCOUNT_PERCENT = 'subs_annual_offer_discount_percent',
    SUBS_MONTHLY_OFFER_DISCOUNT_PERCENT = 'subs_monthly_offer_discount_percent',
    SUBS_ANNUAL_OFFER_DISCOUNT_DURATION = 'subs_annual_offer_discount_duration',
    SUBS_MONTHLY_OFFER_DISCOUNT_DURATION = 'subs_monthly_offer_discount_duration',
    SUBS_ANNUAL_OFFER_NON_DISCOUNT_PRICE = 'subs_annual_offer_non_discount_price',
    SUBS_MONTHLY_OFFER_NON_DISCOUNT_PRICE = 'subs_monthly_offer_non_discount_price',
}

/**
 * Gets the correctly formatted HTML target value for an anchor tag from the notice target which is either 'self' or 'new_tab'
 * @param noticeTarget
 */
export const getHTMLTarget = (noticeTarget: string) => {
    return noticeTarget === 'self' ? '_self' : '_blank';
};

/**
 * React hook that interpolates subscription price box notice data with values from ConsumerSubscriptionPlan
 * @param consumerSubscriptionPlan The subscription plan containing pricing information, can be undefined
 * @param notice The notice template to interpolate with pricing information, can be undefined
 * @returns The interpolated notice data or undefined if interpolation fails
 */
export const useInterpolatedSubscriptionNotice = (
    consumerSubscriptionPlan?: ConsumerSubscriptionPlan,
    notice?: SubscriptionPriceBoxNoticeData,
): SubscriptionPriceBoxNoticeData | undefined => {
    // Always call hooks at the top level
    const formatCurrencyFn = useFormatCurrency();

    // Early return if either parameter is missing (after hooks)
    if (!consumerSubscriptionPlan || !notice) {
        return undefined;
    }

    // Create a wrapper function that matches the expected signature
    const formatCurrency = (price: string): string => formatCurrencyFn.formatCurrency(price);

    /**
     * Extracts all the supported discount / pricing values for monthly and annual plan options
     */
    const extractInterpolationKeysMap = () => {
        const formatCurrencyField = (price?: string) => {
            return price ? formatCurrency(price) : undefined;
        };

        return {
            [NoticeInterpolation.SUBS_ANNUAL_OFFER_DISCOUNT_PRICE]: formatCurrencyField(
                consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPrice,
            ),
            [NoticeInterpolation.SUBS_ANNUAL_OFFER_MONTHLY_DISCOUNT_PRICE]: formatCurrencyField(
                consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountMonthlyPrice,
            ),
            [NoticeInterpolation.SUBS_ANNUAL_OFFER_MONTHLY_NON_DISCOUNT_PRICE]: formatCurrencyField(
                consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountMonthlyPrice ??
                    consumerSubscriptionPlan?.annualPlanOption?.monthlyPrice.amount,
            ),
            [NoticeInterpolation.SUBS_MONTHLY_OFFER_DISCOUNT_PRICE]: formatCurrencyField(
                consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPrice,
            ),
            [NoticeInterpolation.SUBS_ANNUAL_OFFER_DISCOUNT_PERCENT]:
                consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPercent,
            [NoticeInterpolation.SUBS_MONTHLY_OFFER_DISCOUNT_PERCENT]:
                consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPercent,
            [NoticeInterpolation.SUBS_ANNUAL_OFFER_DISCOUNT_DURATION]:
                consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountInterval?.duration,
            [NoticeInterpolation.SUBS_MONTHLY_OFFER_DISCOUNT_DURATION]:
                consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountInterval?.duration,
            [NoticeInterpolation.SUBS_ANNUAL_OFFER_NON_DISCOUNT_PRICE]: formatCurrencyField(
                consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountPrice ??
                    consumerSubscriptionPlan.annualPlanOption?.listPrice.amount,
            ),
            [NoticeInterpolation.SUBS_MONTHLY_OFFER_NON_DISCOUNT_PRICE]: formatCurrencyField(
                consumerSubscriptionPlan.monthlyDiscount?.subscriptionNonDiscountPrice ??
                    consumerSubscriptionPlan.monthlyPlanOption.listPrice.amount,
            ),
        };
    };

    const extractedSubscriptionValues = extractInterpolationKeysMap();

    // Function to interpolate discount / pricing values from the fetched ConsumerSubscriptionPlan for any of
    // the possible interpolation keys as defined in NoticeInterpolation
    const interpolateSubscriptionDiscountKeys = (template: string) => {
        return template.replace(/%\(\w+\)s/g, function (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return String((extractedSubscriptionValues as any)[e.slice(2, -2)]);
        });
    };

    // Function to check if any of the fields in the interpolated notice contain undefined or null to safeguard against
    // situations in which the GraphQL response and notice responses are inconsistent.
    const checkNoticeFieldsForUndefinedOrNull = (
        subscriptionNotice: SubscriptionPriceBoxNoticeData,
    ) => {
        return Object.values(subscriptionNotice).find((field) => {
            if (typeof field === 'string') {
                return field.includes('undefined') || field.includes('null');
            }
            return false;
        });
    };

    const {title, subtitle, subtitle_link_text, cta_1_text, cta_1_target, tagline} = notice;
    const interpolatedPriceBoxNotice = {
        ...notice,
        title: interpolateSubscriptionDiscountKeys(title),
        subtitle: interpolateSubscriptionDiscountKeys(subtitle),
        subtitle_link_text: interpolateSubscriptionDiscountKeys(subtitle_link_text),
        cta_1_text: interpolateSubscriptionDiscountKeys(cta_1_text),
        cta_1_target: getHTMLTarget(cta_1_target),
        tagline: interpolateSubscriptionDiscountKeys(tagline),
    };

    return !checkNoticeFieldsForUndefinedOrNull(interpolatedPriceBoxNotice)
        ? interpolatedPriceBoxNotice
        : undefined;
};
